import { useCallback, useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import env from "@common/environments";
import {
  CommonPassthroughState,
  getIntegrationTypeName,
  INTEGRATION_TO_HASH,
  IntegrationType,
  OAuth2CallbackParams,
  OAuth2PassthroughState,
  SentryCallbackParams,
} from "@common/models/integrations";
import { b64_to_utf8 } from "@common/util/encode";
import useWorkspaceAppsApiConsumer from "@main/consumers/useWorkspaceAppsApiConsumer";
import useWorkspaceDataApiConsumer from "@main/consumers/useWorkspaceDataApiConsumer";
import {
  extractCallbackParams,
  extractPassthroughState,
  extractStringifiedState,
  getHashParams,
  typeToIntegrationType,
} from "@main/layouts/util/extractingFunctions";
import { RoutePaths } from "@main/router/config/routePaths";
import { isAxiosError } from "axios";
import { generatePath, useNavigate, useSearchParams } from "react-router-dom";

import { useIntegrationActivationDeps } from "../IntegrationActivation";
import { IntegrationActivationService } from "../services/IntegrationActivationService";

export const useIntegrationActivationConsumer = () => {
  const navigate = useNavigate();
  const { service } = useIntegrationActivationDeps();
  const { fetchAllApps } = useWorkspaceAppsApiConsumer();
  const { fetchWorkspaceDataAndDispatch } = useWorkspaceDataApiConsumer();

  const [searchParams] = useSearchParams();
  let integrationType = typeToIntegrationType(searchParams.get("type"));

  // Fallback: Extract type from state if query parameter is missing (for Notion OAuth)
  if (!integrationType) {
    const stateParam = searchParams.get("state");
    if (stateParam) {
      try {
        // Try direct JSON parse
        const state = JSON.parse(stateParam) as Partial<OAuth2PassthroughState>;
        if (state.integrationType) {
          integrationType = state.integrationType;
        }
      } catch (e) {
        // Try base64 decode
        try {
          const jsonString = b64_to_utf8(stateParam);
          const state = JSON.parse(jsonString) as Partial<OAuth2PassthroughState>;
          if (state.integrationType) {
            integrationType = state.integrationType;
          }
        } catch (e) {
          // Failed to extract type from state, will show error
        }
      }
    }
  }

  const [invalid, setInvalid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("Something went wrong. Try again.");

  const activate = useCallback(
    async (type: IntegrationType) => {
      try {
        const callbackState = extractStringifiedState(searchParams, type);

        if (!callbackState && type === IntegrationType.SENTRY) {
          setLoading(false);
          setInvalid(false);
          return;
        }

        if (!callbackState) throw new Error("Missing state");

        const passthroughState = extractPassthroughState(callbackState, type);

        if (!passthroughState) throw new Error("Missing passthrough state info");

        const { workspaceSlug, appId, workspaceId } = passthroughState;

        await activateIntegrationByType(searchParams, service, type, passthroughState);

        let appKey;

        await fetchAllApps(workspaceId).then((allApps) => {
          appKey = allApps.find((app) => app.id === appId)?.key;
        });
        await fetchWorkspaceDataAndDispatch();

        if (integrationType === IntegrationType.SENTRY)
          navigate(
            generatePath(RoutePaths.ADMINISTRATION_EXTENSIONS, {
              workspaceSlug,
            }).concat(`?type=${type}`),
            {
              replace: true,
            },
          );
        else if (appKey) {
          navigate(
            generatePath(RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK_KEY, {
              workspaceSlug: workspaceSlug,
              appKey: appKey,
            }).concat(`?type=${INTEGRATION_TO_HASH[type]}`),
            {
              replace: true,
            },
          );
        } else
          navigate(
            generatePath(RoutePaths.ADMINISTRATION_APPS_USER_FEEDBACK, {
              workspaceSlug,
            }),
            {
              replace: true,
            },
          );

        displayToast({
          title: "Fantastic!",
          content: `${getIntegrationTypeName(integrationType)} successfully integrated.`,
        });
      } catch (error) {
        setLoading(false);
        setInvalid(true);
        console.debug(error);
        if (isAxiosError(error) && error.response && error.response.data.message)
          setError(error.response.data.message as string);
      }
    },
    // eslint-disable-next-line
    [searchParams, integrationType, navigate, service, fetchAllApps],
  );

  useEffect(() => {
    if (!integrationType) {
      setLoading(false);
      setInvalid(true);
      return;
    }
    activate(integrationType);
  }, [integrationType, activate]);

  return { invalid, loading, setLoading, integrationType, error };
};

interface TrelloHashParams {
  token: string;
}

function activateIntegrationByType(
  params: URLSearchParams,
  service: IntegrationActivationService,
  type: IntegrationType,
  passthroughState: CommonPassthroughState,
) {
  const { workspaceId, appId } = passthroughState;

  switch (type) {
    case IntegrationType.CLICKUP: {
      const { code } = extractCallbackParams(params, type) as OAuth2CallbackParams;
      if (!code) throw new Error("Missing code");
      return service.activateClickupIntegration(workspaceId, type, appId, {
        authorization_code: code,
        client_id: env.clickupClientId,
      });
    }
    case IntegrationType.NOTION: {
      const { code } = extractCallbackParams(params, type) as OAuth2CallbackParams;
      if (!code) throw new Error("Missing code");

      const { stateId } = passthroughState as OAuth2PassthroughState;
      if (!stateId) throw new Error("Missing state identifier");

      return service.activateNotionIntegration(workspaceId, type, {
        authorization_code: code,
        state: stateId,
      });
    }
    case IntegrationType.SENTRY: {
      const { code, installationId } = extractCallbackParams(params, type) as SentryCallbackParams;
      if (!code) throw new Error("Missing code");
      return service.activateSentryIntegration(workspaceId, type, {
        authorization_code: code,
        installation_id: installationId,
        client_id: env.sentryClientId,
      });
    }
    case IntegrationType.TRELLO:
      const hash = window.location.hash;
      const tokenObject = getHashParams<TrelloHashParams>(hash);

      if (!tokenObject.token) throw new Error("Missing token");

      return service.activateTrelloIntegration(workspaceId, type, appId, {
        api_key: env.trelloAppKey,
        api_token: tokenObject.token,
      });
    default: {
      const { code } = extractCallbackParams(params, type) as OAuth2CallbackParams;
      if (!code) throw new Error("Missing code");

      const { stateId } = passthroughState as OAuth2PassthroughState;
      if (!stateId) throw new Error("Missing state identifier");

      return service.activateIntegration(workspaceId, type, {
        authorization_code: code,
        state: stateId,
      });
    }
  }
}
